import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { FaWhatsapp } from "react-icons/fa";
import {
  MdSms,
  MdEventBusy,
  MdAccessTime,
  MdWorkspacePremium,
  MdClose,
  MdArrowBack,
} from "react-icons/md";
import RenewPlan from "../RenewPlan/RenewPlan";
import AddCredit from "../AddCredit/AddCredit";
import { updateSubscriptionState } from "../../../../store/slice/auth-slice";
import { daysLeft } from "../../../../utils/istDay";
import { refreshProfile } from "../../../../utils/refreshProfile";
import { ackCreditAlerts } from "../../../../api/settings/Credits/credits";
import {
  UrgencyIcon,
  HighlightedText,
  Illustration,
} from "./AlertArt";

/* ------------------------------------------------------------------ */
/* WHEN does a notice appear — change the rules here and nowhere else. */
/* ------------------------------------------------------------------ */
const TRIAL_NOTICE_DAYS = 1; // trial: the day before the end date, and that day
const GRACE_REPROMPT_MINUTES = 10; // while in grace: show the notice again this often

const longDate = (value) =>
  new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

/**
 * What needs the owner's attention, most urgent first.
 *
 * kind:  "credits" | "subscription" | "trial"
 * state: "over" | "low" | "expired" | "due" | "ended" | "ending"
 */
export const deriveAlerts = (auth) => {
  const alerts = [];
  // Trials and plans run THROUGH their end date, so everything below counts
  // Indian calendar days (0 = the last valid day).
  const subDays = daysLeft(auth.subscription_expires_iso);
  const trialDays = daysLeft(auth.demo_end_at);

  if (subDays !== null && subDays < 0) {
    // Past the end date but inside the grace period (beyond it the account
    // is locked to the renew screen and this notice is never reached).
    alerts.push({
      id: "subscription",
      kind: "subscription",
      state: "grace",
      urgent: true,
      date: new Date(auth.subscription_expires_iso),
      pauseOn: auth.grace_ends_on ? new Date(auth.grace_ends_on) : null,
      days: 0,
      action: "renew",
    });
  } else if (subDays !== null) {
    // Paid plan, still valid: the server says when a reminder is due (30, 14,
    // 7, 3, 2, 1 and 0 days before the end, once each).
    const due = auth.subscription_alert;
    if (due) {
      alerts.push({
        id: "subscription",
        kind: "subscription",
        state: "due",
        urgent: due.days <= 7,
        date: new Date(auth.subscription_expires_iso),
        days: due.days,
        action: "renew",
      });
    }
  } else if (trialDays !== null) {
    if (trialDays < 0) {
      alerts.push({
        id: "subscription",
        kind: "trial",
        state: "ended",
        urgent: true,
        date: new Date(auth.demo_end_at),
        days: 0,
        action: "renew",
      });
    } else if (trialDays <= TRIAL_NOTICE_DAYS) {
      alerts.push({
        id: "subscription",
        kind: "trial",
        state: "ending",
        urgent: true,
        date: new Date(auth.demo_end_at),
        days: trialDays,
        action: "renew",
      });
    }
  }

  // Low-credit notices come from the server, which applies the tier and
  // once-a-day rules (utils/creditAlerts.js) and remembers what was shown.
  for (const c of auth.credit_alerts || []) {
    alerts.push({
      id: c.channel,
      kind: "credits",
      channel: c.channel,
      label: c.label,
      state: c.kind === "stopped" ? "over" : "low",
      urgent: c.kind === "stopped",
      balance: Number(c.balance) || 0,
      tier: c.tier,
      action: "recharge",
    });
  }

  return alerts.sort((a, b) => Number(b.urgent) - Number(a.urgent));
};

/* ------------------------------------------------------------------ */
/* How a notice looks: calm pastel surfaces in the brand palette.      */
/* ------------------------------------------------------------------ */

const BRAND = "#0285c7";
const BRAND_DEEP = "#0369a1";
const INK = "#2F3A45";
const MUTED = "#6B7683";
const LINE = "#E6EBF0";

// Inline colours: the app's Tailwind palette is custom, so stock colour
// utilities are not available. Each theme is a soft tint plus one accent.
const THEMES = {
  rose: { tint: "#FDEEEE", tintTo: "#FFF7F5", accent: "#C94B4B", chip: "#FADADA" },
  amber: { tint: "#FFF5E1", tintTo: "#FFFBF2", accent: "#B7791F", chip: "#FCE8BD" },
  blue: { tint: "#E3F1F9", tintTo: "#F3F9FD", accent: BRAND, chip: "#CFE7F5" },
  orchid: { tint: "#FBEAF8", tintTo: "#FEF6FD", accent: "#B93FA3", chip: "#F6D6F0" },
};

const shortDate = (value) =>
  new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });

// kind: which art/icon family; count: the big highlighted figure; hl: pieces
// of the sentence picked out in the accent colour.
const COPY = (a) => {
  switch (`${a.kind}:${a.state}`) {
    case "credits:over":
      return {
        theme: "rose",
        art: "credits",
        Icon: a.channel === "whatsapp" ? FaWhatsapp : MdSms,
        title: `Your ${a.label} messages have stopped`,
        text: `Your ${a.label} credits are over, so ${a.label} messages to your patients are not being sent. Recharge to resume.`,
        hl: ["credits are over", "not being sent"],
        count: { big: "0", small: "messages left" },
        cta: "Recharge",
        chip: "Credits over",
      };
    case "credits:low":
      return {
        theme: "amber",
        art: "credits",
        Icon: a.channel === "whatsapp" ? FaWhatsapp : MdSms,
        title:
          a.tier <= 50
            ? `Your ${a.label} credits are almost over`
            : `Your ${a.label} credits are running low`,
        text: `Only ${a.balance.toLocaleString("en-IN")} ${a.label} messages left. Recharge so patient messages keep going out.`,
        hl: [`${a.balance.toLocaleString("en-IN")} ${a.label} messages`],
        count: {
          big: a.balance.toLocaleString("en-IN"),
          small: "messages left",
        },
        cta: "Recharge",
        chip: `Below ${a.tier}`,
      };
    case "subscription:grace": {
      const pause = a.pauseOn ? longDate(a.pauseOn) : null;
      return {
        theme: "rose",
        art: "time",
        Icon: MdEventBusy,
        title: "Your subscription has expired",
        text: `Your plan ended on ${longDate(a.date)}. You are in a short grace period${
          pause
            ? ` — your account will be paused after ${pause}`
            : " and your account will be paused shortly"
        }. Renew now to avoid interruption.`,
        hl: [longDate(a.date), pause].filter(Boolean),
        count: pause
          ? { big: shortDate(a.pauseOn), small: "account pauses after" }
          : { big: "Grace", small: "period" },
        cta: "Renew plan",
        chip: "Grace period",
      };
    }
    case "subscription:due":
      return {
        theme: "blue",
        art: "time",
        Icon: MdWorkspacePremium,
        title:
          a.days === 0
            ? "Your subscription ends today"
            : a.days === 1
            ? "Your subscription ends tomorrow"
            : `Your subscription ends in ${a.days} days`,
        text: `Your plan ends on ${longDate(a.date)}. Renew now so there is no break in service.`,
        hl: [longDate(a.date)],
        count:
          a.days === 0
            ? { big: "Today", small: "last day of your plan" }
            : a.days === 1
            ? { big: "1", small: "day left" }
            : { big: `${a.days}`, small: "days left" },
        cta: "Renew plan",
        chip:
          a.days === 0
            ? "Ends today"
            : `${a.days} day${a.days === 1 ? "" : "s"} left`,
      };
    case "trial:ending":
      return {
        theme: "orchid",
        art: "time",
        Icon: MdAccessTime,
        title:
          a.days === 0
            ? "Your free trial ends today"
            : "Your free trial ends tomorrow",
        text: "Subscribe now to keep your clinic data and all features without a break.",
        hl: ["keep your clinic data"],
        count:
          a.days === 0
            ? { big: "Today", small: "last day of your trial" }
            : { big: "1", small: "day left" },
        cta: "Subscribe now",
        chip: a.days === 0 ? "Ends today" : "Ends tomorrow",
      };
    case "trial:ended":
    default:
      return {
        theme: "rose",
        art: "time",
        Icon: MdEventBusy,
        title: "Your free trial has ended",
        text: "Choose a plan to continue using Simpld.",
        hl: ["Choose a plan"],
        count: { big: "Ended", small: "free trial" },
        cta: "Choose a plan",
        chip: "Ended",
      };
  }
};

export const Hero = ({ alert, onAction, onDismiss, hasMore }) => {
  const c = COPY(alert);
  const t = THEMES[c.theme];
  const Icon = c.Icon;
  return (
    <div
      className="relative overflow-hidden px-6 py-8 sm:px-10 sm:py-10"
      style={{ background: `linear-gradient(135deg, ${t.tint} 0%, ${t.tintTo} 100%)` }}
    >
      {/* minimal illustration, top right */}
      <div
        className="pointer-events-none absolute right-6 top-6 hidden w-[150px] sm:block"
        style={{ opacity: 0.95 }}
      >
        <Illustration kind={c.art} accent={t.accent} tint={t.chip} />
      </div>

      <button
        type="button"
        aria-label="Close"
        onClick={onDismiss}
        className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full"
        style={{ color: MUTED }}
      >
        <MdClose size={20} />
      </button>

      <div className="relative flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:pr-[150px]">
        <div className="relative shrink-0">
          <div
            className="flex h-16 w-16 items-center justify-center rounded-full bg-white"
            style={{ color: t.accent, boxShadow: "0 4px 14px rgba(47,58,69,0.08)" }}
          >
            <Icon size={30} />
          </div>
          {/* urgency badge on the icon */}
          <span
            className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-white"
            style={{ boxShadow: "0 2px 8px rgba(47,58,69,0.15)" }}
          >
            <UrgencyIcon kind={alert.kind === "credits" ? "credits" : "time"} color={t.accent} size={17} />
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold"
            style={{ background: t.chip, color: t.accent }}
          >
            <UrgencyIcon kind={alert.kind === "credits" ? "credits" : "time"} color={t.accent} size={14} />
            {c.chip}
          </span>
          <h2
            className="mt-2 text-[22px] font-semibold leading-snug sm:text-[26px]"
            style={{ color: INK }}
          >
            {c.title}
          </h2>
          <p className="mt-1.5 text-[15px] leading-relaxed" style={{ color: MUTED }}>
            <HighlightedText
              text={c.text}
              highlights={c.hl}
              color={t.accent}
              bg={t.chip}
            />
          </p>
        </div>
      </div>

      <div className="relative mt-7 flex flex-wrap items-center gap-4">
        <div
          className="flex items-center gap-3 rounded-2xl bg-white px-4 py-2.5"
          style={{ boxShadow: "0 4px 14px rgba(47,58,69,0.07)" }}
        >
          <UrgencyIcon kind={alert.kind === "credits" ? "credits" : "time"} color={t.accent} size={26} />
          <div className="leading-tight">
            <div
              className="text-[26px] font-bold"
              style={{ color: t.accent }}
            >
              {c.count.big}
            </div>
            <div className="text-[12px]" style={{ color: MUTED }}>
              {c.count.small}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => onAction(alert.action)}
          className="rounded-xl px-7 py-3 text-[15px] font-semibold text-white transition hover:opacity-95"
          style={{
            background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DEEP} 100%)`,
            boxShadow: "0 6px 16px rgba(2,133,199,0.25)",
          }}
        >
          {c.cta}
        </button>
        {!hasMore && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-[14px] font-medium"
            style={{ color: MUTED }}
          >
            Remind me later
          </button>
        )}
      </div>
    </div>
  );
};

export const Row = ({ alert, onAction }) => {
  const c = COPY(alert);
  const t = THEMES[c.theme];
  const Icon = c.Icon;
  return (
    <div className="flex items-center gap-3 py-3">
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
        style={{ background: t.tint, color: t.accent }}
      >
        <Icon size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium" style={{ color: INK }}>
          {c.title}
        </p>
        <p
          className="flex items-center gap-1 text-[12px] font-semibold"
          style={{ color: t.accent }}
        >
          <UrgencyIcon
            kind={alert.kind === "credits" ? "credits" : "time"}
            color={t.accent}
            size={13}
            animate={false}
          />
          {c.chip}
        </p>
      </div>
      <button
        type="button"
        onClick={() => onAction(alert.action)}
        className="shrink-0 rounded-lg border px-4 py-1.5 text-[13px] font-semibold transition hover:bg-[#F3F9FD]"
        style={{ color: BRAND, borderColor: "#BFDDEE" }}
      >
        {c.cta}
      </button>
    </div>
  );
};

/**
 * The notice shown once after login to the clinic owner/admin: subscription
 * due or expired, free trial ending, message credits low or over. Renew /
 * Recharge open inside the same card, so nobody has to hunt for Settings.
 */
const AccountStatusPrompt = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("alerts"); // alerts | renew | recharge

  // Only the people who manage the account see it (not pharmacy / staff).
  const isOwner =
    typeof auth?.role === "string" &&
    (auth.role.startsWith("Owner") || auth.role === "Admin");

  const alerts = useMemo(
    () => (auth?.loggedIn ? deriveAlerts(auth) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      auth?.loggedIn,
      auth?.subscription_expires_iso,
      auth?.demo_end_at,
      auth?.credit_alerts,
      auth?.subscription_alert,
      auth?.grace_ends_on,
    ]
  );

  // Once per login: keyed to the session token so a new login (or switching
  // clinic) shows it again, while page changes and refreshes do not.
  const shownKey = auth?.token
    ? `simpld.statusPrompt.${String(auth.token).slice(-16)}`
    : null;

  useEffect(() => {
    if (!isOwner || !shownKey || alerts.length === 0) return;
    try {
      if (sessionStorage.getItem(shownKey)) return;
      sessionStorage.setItem(shownKey, "1");
    } catch (e) {
      /* storage unavailable: show it, worst case once per page load */
    }
    setView("alerts");
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, shownKey, alerts.length]);

  const close = () => {
    setOpen(false);
    setView("alerts");
  };

  // Tell the server which low-credit notices are now on screen, so they are
  // not shown again today (even from another device).
  useEffect(() => {
    if (!open || !isOwner) return;
    const channels = alerts
      .filter((a) => a.kind === "credits")
      .map((a) => a.channel);
    const subscription = alerts.some(
      (a) => a.kind === "subscription" && a.state === "due"
    );
    if (channels.length || subscription) {
      ackCreditAlerts(channels, subscription).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The grace-period banner can open this straight on the renew screen.
  useEffect(() => {
    const onRequest = (e) => {
      setView(e.detail?.view || "renew");
      setOpen(true);
    };
    window.addEventListener("simpld:open-account-prompt", onRequest);
    return () =>
      window.removeEventListener("simpld:open-account-prompt", onRequest);
  }, []);

  // In the grace period the notice comes back every few minutes, so it cannot
  // be forgotten behind a day of work.
  useEffect(() => {
    if (!isOwner || !auth?.in_grace) return undefined;
    const timer = setInterval(() => {
      setView("alerts");
      setOpen(true);
    }, GRACE_REPROMPT_MINUTES * 60 * 1000);
    return () => clearInterval(timer);
  }, [isOwner, auth?.in_grace]);

  // Escape closes it.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!isOwner || !open) return null;

  const [primary, ...rest] = alerts;
  const subTitle = view === "renew" ? "Renew Subscription" : "Add Credits";

  return ReactDOM.createPortal(
    <div
      style={{ zIndex: 99999, background: "rgba(47,58,69,0.35)" }}
      className="fixed inset-0 flex items-start justify-center overflow-y-auto p-3 sm:items-center sm:p-6"
    >
      <ToastContainer />
      <div
        role="dialog"
        aria-modal="true"
        className="my-auto w-full max-w-[640px] overflow-hidden rounded-3xl bg-white"
        style={{ boxShadow: "0 24px 60px rgba(47,58,69,0.18)" }}
      >
        {view === "alerts" && primary && (
          <>
            <Hero
              alert={primary}
              onAction={setView}
              onDismiss={close}
              hasMore={rest.length > 0}
            />

            {rest.length > 0 && (
              <div className="px-6 pb-2 pt-3 sm:px-10">
                <div className="divide-y" style={{ borderColor: LINE }}>
                  {rest.map((alert) => (
                    <Row key={alert.id} alert={alert} onAction={setView} />
                  ))}
                </div>
                <div className="flex justify-end py-3">
                  <button
                    type="button"
                    onClick={close}
                    className="text-[14px] font-medium"
                    style={{ color: MUTED }}
                  >
                    Remind me later
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {view !== "alerts" && (
          <>
            <div
              className="flex items-center justify-between px-5 py-3"
              style={{ background: "#F3F9FD", borderBottom: `1px solid ${LINE}` }}
            >
              <button
                type="button"
                onClick={() => setView("alerts")}
                className="flex items-center gap-1 text-[14px] font-medium"
                style={{ color: BRAND }}
              >
                <MdArrowBack size={18} /> Back
              </button>
              <span className="text-[15px] font-semibold" style={{ color: INK }}>
                {subTitle}
              </span>
              <button
                type="button"
                aria-label="Close"
                onClick={close}
                className="flex h-8 w-8 items-center justify-center rounded-full"
                style={{ color: MUTED }}
              >
                <MdClose size={18} />
              </button>
            </div>
            <div className="px-5 py-6 sm:px-9">
              {view === "renew" && <RenewPlan onClose={close} />}
              {view === "recharge" && (
                <AddCredit
                  onClose={close}
                  balances={{
                    whatsapp: Number(auth.whatsapp_credit_balance) || 0,
                    sms: Number(auth.sms_credit_balance) || 0,
                  }}
                  onCredited={(order) => {
                    if (order?.balances) {
                      dispatch(
                        updateSubscriptionState({
                          whatsapp_credit_balance: order.balances.whatsapp,
                          sms_credit_balance: order.balances.sms,
                        })
                      );
                    }
                    // Full refresh, so the low-credit notice is not shown again.
                    refreshProfile().catch(() => {});
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AccountStatusPrompt;
